/* eslint no-param-reassign: "error" */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getInitialDataV2 } from '../../services/authService';
import { requestStatusType } from '../../types';
import { setFolders } from '../library/librarySlice';
import { setUser } from '../settings/settingsSlice';
import { setSubscriptionState } from '../plan/planSlice';
import { getTeamPictureUrlThunk, setTeamState } from '../team/teamSlice';
import { setGlobalSettings } from '../globalSettings/globalSettingsSlice';
import { removeToken, shouldRefreshToken } from '../../lib/localStorageHandler';
import { refreshToken } from '../auth/authThunks';
import logger from '../../lib/logger';
import { getPluginData } from '../plugin/pluginSlice';
import { sendEmail } from '../../services/emailService';
import StringExtension from '../../utils/secure';
import { setSentryPlan, setSentryTeam, setSentryUser } from '../../lib/sentry';

export interface InitialRequestOptions {
  joinCode?: string;
  zoomCode?: string;
  state?: string;
}
export const getInitialState = createAsyncThunk(
  'app/getInitialState',
  async (options: InitialRequestOptions = {}, { dispatch }) => {
    try {
      // Check if token needs refresh before loading initial data
      if (shouldRefreshToken()) {
        await dispatch(refreshToken());
      }

      const { joinCode, zoomCode, state } = options;

      // Only pass `joinCode` or `zoomCode` to the API if provided
      const apiOptions: InitialRequestOptions = {};
      if (joinCode) {
        apiOptions.joinCode = new StringExtension(joinCode).sanitize();
      }
      if (zoomCode) {
        apiOptions.zoomCode = new StringExtension(zoomCode).sanitize();
      }
      if (state) {
        apiOptions.state = new StringExtension(state).sanitize();
      }

      // Fetch initial data based on the options
      const {
        folders,
        user,
        team,
        packageProperties,
        usage,
        settings,
        integrations,
        globalSettings,
      } = (await getInitialDataV2(apiOptions)) || {};

      const {
        _id,
        email,
        profile,
        role,
        deleteRequestedAt,
        marketing = {},
        createdAt,
        tags = {},
        customButtons = [],
      } = user || {};
      const { name, picture, locale } = profile || {};
      const { teams, selectedTeam } = team;

      // Dispatch data to respective slices
      await dispatch(setFolders(folders));
      await dispatch(
        setUser({
          _id,
          email,
          name,
          picture,
          role,
          deleteRequestedAt,
          createdAt,
          tags,
          integrations,
          locale,
          customButtons,
        }),
      );
      await dispatch(
        setTeamState({
          status: 'succeeded',
          teams,
          selectedTeam,
        }),
      );
      await dispatch(
        setSubscriptionState({
          usage,
          packageProperties,
          plan: selectedTeam.subscription,
          selectedPlan: ['BUSINESS', 'GROWTH'].includes(
            selectedTeam.subscription.package,
          )
            ? (selectedTeam.subscription.package as 'BUSINESS' | 'GROWTH')
            : 'GROWTH',
          marketing,
        }),
      );

      await dispatch(getPluginData());

      // Dispatch global settings to Redux state
      if (globalSettings) {
        await dispatch(
          setGlobalSettings({
            aiChatEnabled: globalSettings.aiChatEnabled,
          }),
        );
      }

      // Initialize Tolt if applicable
      if (window.tolt_referral) {
        window.tolt?.signup(user.email);
      }

      // Set Sentry user and team context for error tracking
      if (_id) {
        setSentryUser({ id: _id, email });
      }
      if (selectedTeam?.teamId) {
        setSentryTeam({ teamId: selectedTeam.teamId });
      }
      if (selectedTeam?.subscription?.package) {
        setSentryPlan({ package: selectedTeam.subscription.package });
      }

      if (team.selectedTeam?.teamId) {
        dispatch(getTeamPictureUrlThunk(team.selectedTeam?.teamId));
      }

      // Return settings and global settings for further usage
      return { settings, globalSettings };
    } catch (error) {
      logger.error('initial data error', error);
      removeToken();
      throw error;
    }
  },
);

export const sendEmailThunk = createAsyncThunk(
  'app/sendEmail',
  async (emailData: {
    template: string;
    toEmail: string;
    data: {
      content: string;
      [key: string]: any;
    };
  }) => {
    try {
      const response = await sendEmail(emailData);
      return response.data;
    } catch (error) {
      logger.error('send email error', error);
      throw error;
    }
  },
);

export interface AppState {
  status: requestStatusType;
}

const initialState: AppState = {
  status: 'idle',
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getInitialState.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(getInitialState.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(getInitialState.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(sendEmailThunk.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(sendEmailThunk.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(sendEmailThunk.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export default appSlice.reducer;
